import React, {FormEvent, ReactNode} from "react";
import {CheckboxItem, Icon, styled, theme, Text} from "@reside/ui";
import {
  PermissionPendingSuffix,
  PermissionStatusGroup,
} from "../permissions-macro-checkbox/PermissionPendingSuffix";
import {
  BulkAssignmentVariant,
  formatBulkStatusLabel,
  getBulkAssignmentState,
} from "./bulkPermissions.helpers";

type Props = Readonly<{
  label: ReactNode;
  badgeLabel: string;
  badgeVariant: BulkAssignmentVariant;
  someChecked: boolean;
  everyChecked: boolean;
  isPending?: boolean;
  onGroupChange: (event: FormEvent<HTMLInputElement>) => void;
  children: ReactNode;
}>;

export const BulkPermissionCategoryCard = ({
  label,
  badgeLabel,
  badgeVariant,
  someChecked,
  everyChecked,
  isPending = false,
  onGroupChange,
  children,
}: Props) => (
  <Card>
    <Header>
      <HeaderMain>
        <HeaderCheckbox
          label={<HeaderLabel>{label}</HeaderLabel>}
          checked={someChecked}
          Icon={props =>
            someChecked && !everyChecked ? (
              <Icon {...props} name="minus-1" />
            ) : (
              <Icon {...props} />
            )
          }
          onChange={onGroupChange}
        />
      </HeaderMain>
      <PermissionStatusGroup>
        <CategoryBadge $variant={badgeVariant}>{badgeLabel}</CategoryBadge>
        {isPending ? <PermissionPendingSuffix /> : null}
      </PermissionStatusGroup>
    </Header>
    <Body>{children}</Body>
  </Card>
);

type BulkPermissionRowProps = Readonly<{
  label: ReactNode;
  userIds: ReadonlyArray<string>;
  hasAssignment: (userId: string) => boolean;
  isPending?: boolean;
  onChange: (event: FormEvent<HTMLInputElement>) => void;
}>;

export const BulkPermissionRow = ({
  label,
  userIds,
  hasAssignment,
  isPending = false,
  onChange,
}: BulkPermissionRowProps) => {
  const {variant, assignedCount, total} = getBulkAssignmentState(
    userIds,
    hasAssignment,
  );
  const someChecked = assignedCount > 0;
  const everyChecked = variant === "all";

  return (
    <PermissionRow>
      <LabelArea>
        <CheckboxItem
          label={label}
          checked={someChecked}
          Icon={props =>
            someChecked && !everyChecked ? (
              <Icon {...props} name="minus-1" />
            ) : (
              <Icon {...props} />
            )
          }
          onChange={onChange}
        />
      </LabelArea>
      <PermissionStatusGroup>
        <StatusLabel $variant={variant}>
          {formatBulkStatusLabel(assignedCount, total)}
        </StatusLabel>
        {isPending ? <PermissionPendingSuffix /> : null}
      </PermissionStatusGroup>
    </PermissionRow>
  );
};

export const BulkPermissionCategoryItem = styled.div`
  border-top: 1px solid ${theme.color.gray10};
  padding: 8px 16px;

  label {
    align-items: center;
    display: flex;
    margin: 0;
    width: 100%;
  }

  label > span {
    font-size: 16px;
    line-height: 20px;
    margin-top: 0;
    min-width: 0;
  }
`;

const Card = styled.div`
  border: 1px solid ${theme.color.gray10};
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.div`
  align-items: center;
  background: #f0f5ff;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 8px 16px;
`;

const HeaderMain = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 12px;
  min-width: 0;
`;

const HeaderCheckbox = styled(CheckboxItem)`
  flex: 1;
  min-width: 0;

  label {
    align-items: center;
    display: flex;
    margin: 0;
  }

  label > span {
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    margin-top: 0;
    min-width: 0;
  }
`;

const HeaderLabel = styled(Text)`
  color: ${theme.color.darkBlue100};
`;

const CategoryBadge = styled.span<{$variant: BulkAssignmentVariant}>`
  background: ${({$variant}) =>
    $variant === "all"
      ? theme.color.primary
      : $variant === "partial"
      ? theme.color.orange100
      : theme.color.gray100};
  border-radius: 999px;
  color: #ffffff;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  padding: 4px 10px;
  white-space: nowrap;
`;

const Body = styled.div`
  background: #ffffff;
`;

const PermissionRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  width: 100%;
`;

const LabelArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatusLabel = styled.span<{$variant: BulkAssignmentVariant}>`
  color: ${({$variant}) =>
    $variant === "all"
      ? theme.color.green100
      : $variant === "partial"
      ? theme.color.orange100
      : theme.color.gray100};
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  min-width: 72px;
  text-align: right;
  white-space: nowrap;
`;
